<template>
  <section
    ref="sectionRef"
    class="report-section relative w-full flex flex-col lg:flex-row items-start py-8 md:py-9 lg:py-20 px-4 sm:px-8 lg:px-12 xl:px-[120px] gap-8 lg:gap-12 justify-between"
    :class="{ 'is-visible': isVisible }"
  >
    <!-- Left Section: Problem Statement and Consequences -->
    <div class="flex flex-col items-start gap-8 lg:gap-10 w-full lg:max-w-[588px] lg:flex-1">
      <!-- Title Section -->
      <div class="report-title-wrapper flex flex-col items-start gap-2 w-full" :class="{ 'animate-in': isVisible }">
        <!-- Badge: text-sm/Medium -->
        <h2 class="text-[#D4D8DD] text-xs md:text-sm font-medium leading-5 tracking-wide">
          {{ title }}
        </h2>
        <!-- Title: display-xs/Bold -->
        <h3 class="text-white font-display text-base md:text-xl lg:text-2xl font-bold leading-6 md:leading-7 lg:leading-8">
          {{ subtitle }}
        </h3>
      </div>

      <!-- Description: text-sm/Regular -->
      <p
        class="report-description-wrapper text-[#D4D8DD] text-xs md:text-sm font-normal leading-4 md:leading-5 w-full"
        :class="{ 'animate-in': isVisible }"
        :style="{ transitionDelay: isVisible ? '0.1s' : '0s' }"
      >
        {{ description }}
      </p>

      <!-- Three Consequence Points -->
      <div class="flex flex-col items-start gap-6 w-full">
        <div
          v-for="(point, index) in consequencePoints"
          :key="index"
          class="report-consequence-item-wrapper flex flex-col gap-2 w-full transition-opacity duration-200 hover:opacity-90"
          :class="{ 'animate-in': isVisible }"
          :style="{
            transitionDelay: isVisible ? `${0.2 + index * 0.1}s` : '0s',
          }"
        >
          <div class="flex items-center gap-[10px]">
            <div class="report-icon-wrapper flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0 rounded-lg bg-[#E3323233] p-2 sm:p-3">
              <component :is="resolveIcon(point.icon)" :size="24" class="text-[#E33232]" />
            </div>
            <div class="flex-1">
              <h4 class="text-white text-base sm:text-lg font-semibold leading-tight">
                {{ point.title }}
              </h4>
              <p class="text-[#D4D8DD] text-sm font-normal italic leading-5 mt-1">
                {{ point.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Section: Business Statistics Panel -->
    <div
      class="report-statistics-panel flex flex-col items-start p-5 sm:p-6 pt-6 sm:pt-8 w-full lg:max-w-[588px] lg:flex-1 rounded-2xl min-h-[432px]"
      :class="{ 'animate-in': isVisible }"
      :style="{ transitionDelay: isVisible ? '0.3s' : '0s' }"
    >
      <!-- Section Title -->
      <div class="w-full">
        <h3 class="report-statistics-badge text-white text-xs sm:text-sm font-normal flex flex-row justify-start items-center px-4 py-1.5 gap-2.5 h-8 rounded-full w-fit">THỐNG KÊ DOANH NGHIỆP</h3>
      </div>

      <!-- Performance Metrics -->
      <div class="flex flex-col gap-6 sm:gap-8 w-full mt-6">
        <div
          v-for="(metric, index) in metrics"
          :key="index"
          class="report-metric-item flex flex-col gap-2 w-full"
          :class="{ 'animate-in': isVisible }"
          :style="{
            transitionDelay: isVisible ? `${0.4 + index * 0.1}s` : '0s',
          }"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="text-white text-xs sm:text-sm font-normal leading-5 flex-1">{{ metric.label }}</span>
            <div class="flex items-center gap-2 flex-shrink-0">
              <component :is="metric.trend === 'increase' ? TrendingUp : TrendingDown" :size="16" class="text-[#FF4343] flex-shrink-0" />
              <span class="text-[#FF4343] text-xs sm:text-sm font-medium leading-5 whitespace-nowrap">
                {{ metric.change }}
              </span>
            </div>
          </div>
          <div class="report-progress-bar w-full h-2 rounded-full overflow-hidden">
            <div
              class="report-progress-fill h-full rounded-full transition-all duration-1000 ease-out"
              :style="{
                width: isVisible ? metric.value + '%' : '0%',
                transitionDelay: isVisible ? `${0.5 + index * 0.1}s` : '0s',
              }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Estimated Annual Loss -->
      <div
        class="report-loss-card flex flex-col items-center justify-center py-5 px-4 sm:px-6 lg:px-[94px] gap-2 min-h-[104px] w-full rounded-2xl mt-8 sm:mt-12"
        :class="{ 'animate-in': isVisible }"
        :style="{ transitionDelay: isVisible ? '0.7s' : '0s' }"
      >
        <p class="text-[#D4D8DD] text-xs sm:text-sm font-normal leading-5 text-center">Tổng thiệt hại ước tính mỗi năm</p>
        <p class="report-loss-amount text-2xl sm:text-3xl font-black leading-tight sm:leading-9 text-center">
          {{ estimatedLoss }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { TrendingUp, TrendingDown, UserRoundX, Wallet } from 'lucide-vue-next';

const iconMap = {
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'user-round-x': UserRoundX,
  wallet: Wallet,
};
const resolveIcon = name => iconMap[name] || TrendingDown;

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const sectionContent = computed(() => props.content || {});

const title = computed(() => sectionContent.value.title || sectionContent.value.headline || 'SỰ THẬT PHŨ PHÀNG');
const subtitle = computed(() => sectionContent.value.subtitle || 'NẾU KHÔNG THAY ĐỔI, DOANH NGHIỆP CỦA BẠN SẼ PHẢI TRẢ GIÁ BAO NHIÊU?');
const description = computed(
  () =>
    sectionContent.value.description ||
    sectionContent.value.text ||
    'Mỗi phút khách hàng chờ đợi là một cơ hội bán hàng bị đánh mất. Sự không nhất quán trong dịch vụ làm xói mòn lòng tin và đẩy khách hàng sang đối thủ cạnh tranh.'
);

// Animation state
const isVisible = ref(false);
const sectionRef = ref(null);
let observer = null;

onMounted(() => {
  if (!sectionRef.value) return;

  // Intersection Observer for scroll animation
  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          // Disconnect after first trigger for performance
          if (observer) {
            observer.disconnect();
          }
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px',
    }
  );

  observer.observe(sectionRef.value);
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

// Mock data for consequence points
const consequencePoints = computed(() => {
  const items = Array.isArray(sectionContent.value.consequences) ? sectionContent.value.consequences : [];

  return items.length > 0
    ? items
    : [
        {
          title: 'Thất thoát 30% doanh thu tiềm năng',
          description: 'Do phản hồi chậm trễ và quy trình mua hàng đứt đoạn.',
          icon: 'trending-down',
        },
        {
          title: 'Khách hàng rời bỏ thương hiệu',
          description: '80% người dùng sẵn sàng đổi nhà cung cấp nếu trải nghiệm chat tệ.',
          icon: 'user-round-x',
        },
        {
          title: 'Chi phí vận hành tăng vọt',
          description: 'Lãng phí ngân sách cho các tác vụ thủ công vô nghĩa.',
          icon: 'wallet',
        },
      ];
});

// Mock data for metrics
const metrics = computed(() => {
  const items = Array.isArray(sectionContent.value.metrics) ? sectionContent.value.metrics : [];

  return items.length > 0
    ? items
    : [
        {
          label: 'Mức độ hài lòng khách hàng',
          value: 75,
          change: 'Giảm 25%',
          trend: 'decrease',
        },
        {
          label: 'Tỷ lệ bỏ rơi giỏ hàng',
          value: 60,
          change: 'Giảm 40%',
          trend: 'decrease',
        },
        {
          label: 'Chi phí vận hành',
          value: 40,
          change: 'Tăng 40%',
          trend: 'increase',
        },
      ];
});

const estimatedLoss = computed(() => sectionContent.value.estimatedLoss || 'Hơn 250.000.000 VNĐ');
</script>
